import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useResolvedImageCandidates from "../hooks/useResolvedImageCandidates";
import { buildAdvertiserLogoCandidates } from "../utils/advertiserLogo";
import "./GovernedAdvertiserSelector.css";

export const ADVERTISER_IMAGE_LOAD_TIMEOUT_MS = 8000;

const getAdvertiserName = (advertiser, fallbackName = "") =>
  String(
    advertiser?.name ||
      advertiser?.advertiser ||
      advertiser?.title ||
      fallbackName ||
      ""
  ).trim();

export const getAdvertiserInitials = (name = "") => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "AD";
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

function GovernedAdvertiserLogoMedia({
  apiBase,
  candidates,
  fallbackClass,
  frameClass,
  identitySignature,
  imageClass,
  isTrigger,
  resolvedName,
  variant,
}) {
  const {
    activeIndex = 0,
    candidates: resolvedCandidates = [],
    exhausted = false,
    handleError,
    phase = "idle",
    resolvedSrc = "",
  } = useResolvedImageCandidates(candidates, { apiBase });
  const [readyImage, setReadyImage] = useState({ attemptKey: "", src: "" });
  const [lastFailure, setLastFailure] = useState({
    activeIndex: -1,
    attemptKey: "",
    reason: "",
  });
  const candidateSignature = resolvedCandidates.join("\n");
  const loadSignature = `${identitySignature}\n${candidateSignature}`;
  const attemptKey = `${loadSignature}\n${activeIndex}\n${resolvedSrc}`;
  const currentAttemptRef = useRef(attemptKey);
  const failedAttemptRef = useRef("");
  currentAttemptRef.current = attemptKey;
  const hasCandidates = resolvedCandidates.length > 0;
  const currentFailure =
    lastFailure.attemptKey === attemptKey ? lastFailure.reason : "";
  const isFinalCandidate =
    hasCandidates && activeIndex >= resolvedCandidates.length - 1;
  const resolutionFailed =
    exhausted ||
    phase === "failed" ||
    phase === "error" ||
    Boolean(currentFailure && isFinalCandidate);
  const imageReady =
    !resolutionFailed &&
    !currentFailure &&
    Boolean(resolvedSrc) &&
    readyImage.attemptKey === attemptKey &&
    readyImage.src === resolvedSrc;
  const imageState = !hasCandidates || resolutionFailed
    ? "fallback"
    : imageReady
    ? "image-ready"
    : "loading";
  const fallbackReason = !hasCandidates
    ? "missing"
    : currentFailure ||
      (lastFailure.activeIndex >= resolvedCandidates.length - 1
        ? lastFailure.reason
        : "") ||
      "error";

  const markImageReady = useCallback(() => {
    if (
      !resolvedSrc ||
      currentAttemptRef.current !== attemptKey ||
      failedAttemptRef.current === attemptKey
    ) {
      return;
    }
    setReadyImage({ attemptKey, src: resolvedSrc });
  }, [attemptKey, resolvedSrc]);

  const handleImageRef = useCallback(
    (image) => {
      if (image?.complete && image.naturalWidth > 0) markImageReady();
    },
    [markImageReady]
  );

  const failCurrentCandidate = useCallback(
    (reason) => {
      if (
        currentAttemptRef.current !== attemptKey ||
        failedAttemptRef.current === attemptKey
      ) {
        return;
      }
      failedAttemptRef.current = attemptKey;
      setReadyImage({ attemptKey: "", src: "" });
      setLastFailure({ activeIndex, attemptKey, reason });
      handleError();
    },
    [activeIndex, attemptKey, handleError]
  );

  useEffect(() => {
    if (imageState !== "loading") return undefined;
    const timeoutId = window.setTimeout(
      () => failCurrentCandidate("timeout"),
      ADVERTISER_IMAGE_LOAD_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [failCurrentCandidate, imageState]);
  return (
    <span
      className={frameClass}
      data-advertiser-image-state={imageState}
      data-advertiser-image-fallback-reason={
        imageState === "fallback" ? fallbackReason : undefined
      }
      aria-hidden="true"
    >
      {resolvedSrc && imageState !== "fallback" ? (
        <img
          key={attemptKey}
          ref={handleImageRef}
          src={resolvedSrc}
          alt=""
          aria-hidden="true"
          className={`${imageClass} governed-advertiser-logo-image governed-advertiser-logo-image--${imageState}`}
          draggable={false}
          onLoad={markImageReady}
          onError={() => failCurrentCandidate("error")}
        />
      ) : null}
      {imageState === "loading" ? (
        <span
          className={`governed-advertiser-logo-loading governed-advertiser-logo-loading--${variant}`}
          aria-hidden="true"
        >
          <span className="governed-advertiser-logo-loading-mark" />
        </span>
      ) : null}
      {imageState === "fallback" ? (
        <span className={fallbackClass}>
          {isTrigger ? (
            <>
              <span className="governed-advertiser-initials">
                {getAdvertiserInitials(resolvedName)}
              </span>
              <span className="governed-advertiser-fallback-name">
                {resolvedName}
              </span>
            </>
          ) : (
            getAdvertiserInitials(resolvedName)
          )}
        </span>
      ) : null}
    </span>
  );
}

function GovernedAdvertiserLogo({
  advertiser = null,
  advertiserName = "",
  apiBase = "",
  variant = "option",
}) {
  const resolvedName =
    getAdvertiserName(advertiser, advertiserName) || "Advertiser";
  const candidates = useMemo(
    () =>
      buildAdvertiserLogoCandidates(
        apiBase,
        advertiser,
        advertiser?.raw
      ),
    [advertiser, apiBase]
  );
  const isTrigger = variant === "trigger";
  const identitySignature = useMemo(
    () =>
      [apiBase, advertiser?.id ?? "", resolvedName, ...candidates].join("\n"),
    [advertiser?.id, apiBase, candidates, resolvedName]
  );

  return (
    <GovernedAdvertiserLogoMedia
      key={identitySignature}
      apiBase={apiBase}
      candidates={candidates}
      fallbackClass={
        isTrigger
          ? "governed-advertiser-identity-fallback"
          : "dashboard-advertiser-selector-logo-fallback"
      }
      frameClass={
        isTrigger
          ? "governed-advertiser-identity-media"
          : "dashboard-advertiser-selector-logo-frame"
      }
      identitySignature={identitySignature}
      imageClass={
        isTrigger
          ? "governed-advertiser-identity-logo"
          : "dashboard-advertiser-selector-logo"
      }
      isTrigger={isTrigger}
      resolvedName={resolvedName}
      variant={variant}
    />
  );
}

export function AdvertiserIdentityTrigger({
  advertiser = null,
  advertiserName = "",
  apiBase = "",
  disabled = false,
  empty = false,
  loading = false,
  modalOpen = false,
  onClick,
} = {}) {
  const resolvedName = getAdvertiserName(advertiser, advertiserName);
  const visibleStatus = loading
    ? "Loading advertisers"
    : empty
    ? "No advertisers available"
    : resolvedName || "Choose advertiser";
  const accessibleLabel = resolvedName
    ? `Change advertiser. Current advertiser: ${resolvedName}`
    : empty
    ? "No advertisers available"
    : loading
    ? "Advertiser selection is loading"
    : "Choose advertiser";

  return (
    <button
      type="button"
      className="governed-advertiser-identity-trigger"
      data-advertiser-selector-trigger="true"
      aria-label={accessibleLabel}
      aria-haspopup="dialog"
      aria-expanded={modalOpen ? "true" : "false"}
      aria-controls="dashboard-advertiser-selector"
      aria-busy={loading ? "true" : undefined}
      disabled={disabled || loading || empty}
      onClick={onClick}
    >
      {resolvedName && !loading && !empty ? (
        <GovernedAdvertiserLogo
          key={`${advertiser?.id ?? ""}:${resolvedName}`}
          advertiser={advertiser}
          advertiserName={resolvedName}
          apiBase={apiBase}
          variant="trigger"
        />
      ) : (
        <span
          className="governed-advertiser-identity-empty"
          aria-hidden="true"
        >
          {visibleStatus}
        </span>
      )}
      {!disabled && !loading && !empty ? (
        <span
          className="governed-advertiser-identity-cue"
          aria-hidden="true"
        >
          Change advertiser
        </span>
      ) : null}
    </button>
  );
}

export function GovernedAdvertiserSelectorModal({
  advertisers = [],
  selectedAdvertiserId = null,
  selectedAdvertiserName = "",
  apiBase = "",
  onSelect,
  onClose,
} = {}) {
  const selectedId =
    selectedAdvertiserId == null ? "" : String(selectedAdvertiserId);
  const selectedName = String(selectedAdvertiserName || "").trim();
  const closeButtonRef = useRef(null);
  const optionRefs = useRef([]);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const controlledTrigger = document.querySelector(
      '[aria-controls="dashboard-advertiser-selector"][aria-haspopup="dialog"]'
    );
    const previouslyFocused = controlledTrigger || document.activeElement;
    const focusTarget = () => {
      const selectedOption = optionRefs.current.find(
        (option) => option?.getAttribute("aria-pressed") === "true"
      );
      (selectedOption || optionRefs.current[0] || closeButtonRef.current)?.focus();
    };
    const frame =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(focusTarget)
        : window.setTimeout(focusTarget, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = [
        closeButtonRef.current,
        ...optionRefs.current.filter(Boolean),
      ].filter((element) => element && !element.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(frame);
      } else {
        window.clearTimeout(frame);
      }
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === "function" &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
  }, []);

  const focusOption = (index) => {
    const total = optionRefs.current.filter(Boolean).length;
    if (!total) return;
    const normalizedIndex = (index + total) % total;
    optionRefs.current[normalizedIndex]?.focus();
  };

  return (
    <div
      className="dashboard-advertiser-selector-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        id="dashboard-advertiser-selector"
        className="dashboard-advertiser-selector-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-advertiser-selector-title"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="dashboard-advertiser-selector-close"
          aria-label="Close advertiser selector"
          onClick={onClose}
        >
          x
        </button>
        <div className="dashboard-advertiser-selector-kicker">Account</div>
        <h2 id="dashboard-advertiser-selector-title">Select Advertiser</h2>
        <div className="dashboard-advertiser-selector-list">
          {advertisers.length ? (
            advertisers.map((advertiser, index) => {
              const advertiserId =
                advertiser?.id == null ? "" : String(advertiser.id);
              const advertiserName =
                getAdvertiserName(advertiser) || "Advertiser";
              const selected =
                (selectedId && advertiserId === selectedId) ||
                (!selectedId &&
                  selectedName &&
                  advertiserName === selectedName);

              return (
                <button
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  key={advertiserId || advertiserName}
                  type="button"
                  className={`dashboard-advertiser-selector-option${
                    selected
                      ? " dashboard-advertiser-selector-option--active"
                      : ""
                  }`}
                  aria-pressed={selected ? "true" : "false"}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                      event.preventDefault();
                      focusOption(index + 1);
                    } else if (event.key === "ArrowUp") {
                      event.preventDefault();
                      focusOption(index - 1);
                    } else if (event.key === "Home") {
                      event.preventDefault();
                      focusOption(0);
                    } else if (event.key === "End") {
                      event.preventDefault();
                      focusOption(advertisers.length - 1);
                    }
                  }}
                  onClick={() => onSelect?.(advertiser)}
                >
                  <GovernedAdvertiserLogo
                    advertiser={advertiser}
                    advertiserName={advertiserName}
                    apiBase={apiBase}
                  />
                  <span className="dashboard-advertiser-selector-name">
                    {advertiserName}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="dashboard-advertiser-selector-empty">
              No advertisers available
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
